"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";

type MediaField = {
    media?: {
        file?: string;
        alt?: string;
    };
};

type GuideTab = {
    title?: string;
    video?: MediaField;
    thumbnail?: MediaField;
};

type Props = {
    data: {
        pageHeading?: string | Record<string, string>;
        tabs?: GuideTab[];
        pdf?: MediaField;
        pdfTitle?: string;
    };
};

const slugify = (text = "") =>
    text
        .toLowerCase()
        .replace(/&/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)+/g, "");

/* ---------------- ICONS ---------------- */

const VideoIcon = () => (
    <svg
        className="w-6 h-6 flex-shrink-0 text-gray-700"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={1.8}
    >
        <rect x="3" y="6" width="17" height="14" rx="2" />
        <polygon points="10,9 15,12 10,15" fill="currentColor" stroke="none" />
    </svg>
);

const PdfIcon = () => (
    <div className="flex items-center justify-center w-7 h-7 border rounded bg-gray-100 text-[10px] font-bold">
        PDF
    </div>
);

const DownloadIcon = () => (
    <svg
        className="w-5 h-5 text-gray-700"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={1.8}
    >
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v10m0 0l-4-4m4 4l4-4M4 20h16" />
    </svg>
);

/* ---------------- COMPONENT ---------------- */

export default function ProWhipUserGuide({ data }: Props) {

    const { pageHeading, tabs = [], pdf, pdfTitle } = data;
    const pathname = usePathname();
    const [activeIndex, setActiveIndex] = useState(0);

    const safeHeading =
        typeof pageHeading === "string"
            ? pageHeading
            : pageHeading?.["default"] ?? "";

    useEffect(() => {
        if (!tabs.length) return;

        const segments = pathname.split("/").filter(Boolean);
        const lastSegment = segments[segments.length - 1];

        if (lastSegment === "user-guide") {
            setActiveIndex(0);
            return;
        }

        const index = tabs.findIndex(
            (tab) => slugify(tab.title) === lastSegment
        );

        if (index !== -1) {
            setActiveIndex(index);
        }
    }, [pathname, tabs]);

    if (!tabs.length) return null;

    const activeTab = tabs[activeIndex];
    const videoSrc = activeTab?.video?.media?.file;
    const posterSrc = activeTab?.thumbnail?.media?.file;
    const pdfSrc = pdf?.media?.file;

    const handleTabChange = (index: number) => {
        setActiveIndex(index);

        const slug = slugify(tabs[index].title);
        const basePath = pathname.split("/user-guide")[0];

        window.history.pushState({}, "", `${basePath}/user-guide/${slug}`);
    };

    return (
        <section className="w-full bg-white">

            <div className="flex flex-col lg:flex-row w-full lg:h-[calc(100vh-120px)]">

                {/* VIDEO SECTION */}

                <div className="w-full lg:w-[75%] bg-black">

                    {videoSrc && (
                        <video
                            controls
                            playsInline
                            preload="metadata"
                            poster={posterSrc}
                            className="w-full h-[240px] sm:h-[320px] md:h-[380px] lg:h-full object-cover"
                        >
                            <source src={videoSrc} type="video/mp4" />
                        </video>
                    )}

                </div>

                {/* SIDEBAR */}

                <aside className="w-full lg:w-[25%] border-t lg:border-t-0 lg:border-l px-8 pt-10 flex flex-col bg-[#f3f3f3]">

                    {/* PAGE HEADING */}

                    <h2 className="text-lg font-semibold mt-6 leading-snug">
                        {safeHeading}
                    </h2>

                    {/* VIDEO LIST */}

                    <ul className="space-y-2 mt-6">

                        {tabs.map((tab, index) => {

                            const isActive = index === activeIndex;

                            return (
                                <li key={index}>

                                    <button
                                        onClick={() => handleTabChange(index)}
                                        className={`w-full flex items-center gap-3 text-left px-3 py-2 rounded transition
                    ${isActive
                                                ? "font-semibold text-black bg-[#e8e3a5]"
                                                : "text-gray-700 hover:bg-gray-100"
                                            }`}
                                    >

                                        <VideoIcon />

                                        <span className="text-sm">
                                            {tab.title}
                                        </span>

                                    </button>

                                </li>
                            );
                        })}

                    </ul>

                    {/* PDF DOWNLOAD */}

                    {pdfSrc && (

                        <div className="mt-6 lg:mt-auto border-t bg-[#eaeaea] -mx-8 px-8 py-4">

                            <a
                                href={pdfSrc}
                                download
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center justify-between"
                            >

                                <div className="flex items-center gap-3">

                                    <PdfIcon />

                                    <span className="text-sm text-gray-800">
                                        {pdfTitle || "Quick Guide"}
                                    </span>

                                </div>

                                <DownloadIcon />

                            </a>

                        </div>

                    )}

                </aside>

            </div>

        </section>
    );
}
